import { Stack } from '@chakra-ui/layout';
import {
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  MenuItem,
  Spinner,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FiEye, FiTrash } from 'react-icons/all';
import download from 'downloadjs';
import { Heading, Table, Textarea } from '~/components';
import { FilterContext } from '~/contexts/FilterContext/FilterContext';
import { useSinister } from '~/hooks/useSinisters/useSinisters';
import { GeneralInformation } from '~/pages/Portal/Sinisters/GeneralInformation';
import apiSinister from '~/utils/apiSinister';
import { queryClient } from '~/utils/react-query';
import { SinisterDebitNote } from '~/types/sinisters';
import { getDebitNote } from '~/services/Sinisters';
import { formatCurrency } from '~/utils/formatCurrency';

export const SinistersEvaluatePaymentForm: React.FC<{
  sinisterId: number;
  link?: boolean;
}> = ({ sinisterId, link = false }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef(null);
  const toast = useToast();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm();
  const { errors } = formState;
  const [isFetching, setFetching] = useState(false);

  const [revisao, setRevisao] = useState<boolean>(false);
  const [descricao, setDescricao] = useState<string>('');
  const [show, setShow] = useState(false);
  const { data, isLoading, refetch } = useSinister(sinisterId);
  const [nota_debito, setNotaDebito] = useState<
    | {
        documento: string;
        data: string;
        empresa: string;
        valor: string;
      }
    | undefined
  >(undefined);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (link) handleOpen();
  }, []);

  const handleOpen = () => {
    if (data) {
      const data_vencimento = new Date(
        data.nota_de_debito_data_vencimento,
      ).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
      setNotaDebito({
        documento: data.nota_de_debito_numero,
        valor: formatCurrency(Number(data.nota_de_debito_valor)),
        empresa: data.emitente.nome,
        data: data_vencimento,
      });
    }
    onOpen();
  };

  const handleClose = () => {
    clearValues();
    onClose();
  };
  const clearValues = () => {
    setRevisao(false);
    setDescricao('');
    clearErrors();
  };

  async function handleCreate() {
    clearErrors();

    (!descricao || !descricao.trim().length) &&
      revisao &&
      setError('descricao', {
        type: 'required',
        message: 'Descrição é obrigatória',
      });

    descricao.length > 255 &&
      setError('descricao', {
        type: 'maxLength',
        message: 'Observações com mais de 255 caracteres',
      });

    const { _formState } = control;
    if (!_formState.isValid) return;

    const form = revisao
      ? {
          solicitar_revisao: revisao,
          comentario: descricao,
        }
      : { solicitar_revisao: revisao };

    setFetching(true);

    const request = apiSinister.post(
      `comprovar-pagamento/${data?.id}/avaliar/`,
      form,
    );

    request
      .then(() => {
        toast({
          title: `Pagamento avaliado`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        clearValues();
        onClose();
        queryClient.invalidateQueries('sinistersList');
        queryClient.invalidateQueries(['sinisterLog', sinisterId]);
        queryClient.invalidateQueries(['sinister', sinisterId]);
        queryClient.invalidateQueries('sinisterDashboard');
        queryClient.invalidateQueries(['sinisterDocuments', sinisterId]);
      })
      .catch(error => {
        if (error.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 413) {
          toast({
            title: `Tamanho de requisição excedida`,
            description: 'Limite máximo para tamanho de arquivos é de 5MB',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 400) {
          // erros de valid

          const errorsValidate = Object.entries(error.response.data);

          errorsValidate.forEach(err => {
            toast({
              title: `Erros ao avaliar pagamento`,
              description: String(err[1]),
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
        } else {
          toast({
            title: `Erro ao avaliar pagamento`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }

  function handleDownload() {
    setIsDownloading(true);
    const form: SinisterDebitNote = {
      referente: data!.nota_de_debito_referente,
      data_de_vencimento: data!.nota_de_debito_data_vencimento,
      id_emitente: data!.emitente.id,
      valor: Number(data!.nota_de_debito_valor),
      numero_nota_debito: data!.nota_de_debito_numero!,
    };
    getDebitNote(sinisterId, form)
      .then(r => {
        if (r?.data?.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(err => {
        toast({
          title: 'Erro ao gerar nota de débito',
          description: 'Entre em contato para mais informações',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsDownloading(false);
      });
  }

  function handleDescricao(e: any) {
    setDescricao(e.target.value);
    if (e.target.value.length <= 255) {
      clearErrors('descricao');
    }
  }

  const handleCheckboxChange = () => {
    setRevisao(!revisao);
    clearErrors('descricao');
    setDescricao('');
  };

  return (
    <>
      <MenuItem onClick={handleOpen}>Avaliar pagamento</MenuItem>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="lg"
        closeOnOverlayClick={!isFetching}
      >
        <DrawerOverlay />
        <DrawerContent borderTopLeftRadius="md">
          <DrawerCloseButton isDisabled={isFetching} />
          <DrawerHeader>Avaliar pagamento</DrawerHeader>
          <DrawerBody>
            <Stack gridGap={6} bg="white" maxW={600} w="100%">
              <Stack gridGap={4}>
                <GeneralInformation sinister={data!} isAdmin />
                <Heading>Nota de débito</Heading>
                <Table type="secondary" size="sm">
                  <Thead>
                    <Tr>
                      <Th>Documento</Th>
                      <Th>Data</Th>
                      <Th whiteSpace="nowrap">Empresa a faturar</Th>
                      <Th whiteSpace="nowrap">Valor (R$)</Th>
                      <Th textAlign="center">Ação</Th>
                      <Th />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {nota_debito && (
                      <Tr key={`${1}`}>
                        <Td>{nota_debito.documento}</Td>
                        <Td>{nota_debito.data}</Td>
                        <Td>{nota_debito.empresa}</Td>
                        <Td>{nota_debito.valor}</Td>
                        <Td
                          style={{
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <Tooltip label="Download" placement="top">
                            <IconButton
                              w="25px"
                              fontSize="20px"
                              background="transparent"
                              aria-label="Remove type"
                              icon={<Icon as={FiEye} />}
                              onClick={() => handleDownload()}
                              isDisabled={isDownloading}
                            />
                          </Tooltip>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
                <FormLabel htmlFor="writeUpFile" style={{ fontSize: '14px' }}>
                  Solicitar revisão
                  <Checkbox
                    isChecked={revisao}
                    marginLeft="10px"
                    onChange={handleCheckboxChange}
                    iconColor="black"
                  />
                </FormLabel>
                {revisao && (
                  <FormControl isInvalid={errors.descricao}>
                    <FormLabel
                      htmlFor="writeUpFile"
                      style={{ fontSize: '14px' }}
                      justifyContent="space-between"
                      display="flex"
                    >
                      O que deve ser revisado? *
                    </FormLabel>
                    <Textarea
                      name="descricao"
                      resize="none"
                      error={errors?.descricao}
                      value={descricao}
                      onChange={e => handleDescricao(e)}
                      fontSize="15px"
                      placeholder="Entre com a descrição..."
                      isDisabled={isFetching}
                    />
                  </FormControl>
                )}
              </Stack>
            </Stack>
            <Button
              type="submit"
              onClick={handleSubmit(handleCreate)}
              color="white"
              background="gray.900"
              _hover={{
                bgColor: 'black',
              }}
              style={{ marginTop: '20px' }}
              isDisabled={isFetching}
            >
              {revisao ? 'Enviar ao cliente' : 'Confirmar'}{' '}
              {isFetching && <Spinner style={{ marginLeft: '10px' }} />}
            </Button>
            <Button
              type="submit"
              onClick={handleClose}
              color="black"
              background="transparent"
              style={{
                marginTop: '20px',
                marginLeft: '10px',
              }}
              isDisabled={isFetching}
            >
              Cancelar
            </Button>
            {revisao && (
              <Text
                style={{
                  fontSize: '12px',
                  color: 'gray.500',
                  marginTop: '20px',
                }}
              >
                * Campos obrigatórios
              </Text>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
